import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faStar, faTimes } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, InfoPopup } from "../../components";
import { CancellationType } from "../../enums";
import { CancellationPerk, CancellationPerks } from "../../types";
import { POSSIBLE_CHOICES_LABELS } from "./constants";
import "./DesktopPerksMatrix.scss";

interface Props {
  cancellationPerks: CancellationPerks;
  onChangeCancellationType: (cancellationType: CancellationType) => void;
  selectedCancellationType?: CancellationType;
}

const DesktopPerksMatrix = ({
  cancellationPerks,
  onChangeCancellationType,
  selectedCancellationType,
}: Props) => {
  const { t } = useTranslation();

  const getPerkValue = useCallback(
    (perk: CancellationPerk) => (type: CancellationType) => {
      const value = (perk as any)[type.toLowerCase()];
      switch (value) {
        case "false":
          return <FontAwesomeIcon color="#AFADAD" icon={faTimes} />;
        case "true":
          return <FontAwesomeIcon color="#7EB441" icon={faCheck} />;
        default:
          return value;
      }
    },
    []
  );

  const voucherFaqUrl = useMemo(() => {
    const label = t("faq_page_url_voucher");
    return !!label && label !== "faq_page_url_voucher" ? label : undefined;
  }, [t]);

  return (
    <div className="desktop-perks-matrix">
      <table cellPadding={0} cellSpacing={0} className="perks-table">
        <tbody>
          <tr>
            <th />
            {cancellationPerks.possibleChoices.map((possibleChoice, index) => (
              <td className={`perk-cell ${index === 0 ? "most-popular" : ""}`} key={index}>
                <div
                  className="perk-cell__header"
                  onClick={() => onChangeCancellationType(possibleChoice)}
                >
                  {index === 0 && <div className="ribbon">{t("lp_most_chosen")}</div>}
                  <div>
                    {Array(cancellationPerks.possibleChoices.length - index)
                      .fill(null)
                      .map((_, starIndex) => (
                        <FontAwesomeIcon color="#fcc001" icon={faStar} key={starIndex} />
                      ))}
                  </div>
                  <div className="perk-cell__header-title">
                    {t(POSSIBLE_CHOICES_LABELS[possibleChoice])}
                  </div>
                </div>
              </td>
            ))}
          </tr>
          {cancellationPerks.perks.map((perk, rowIndex) => (
            <tr className="perk-row" key={rowIndex}>
              <th>
                {t(perk.title)}
                {perk.description && <InfoPopup>{t(perk.description)}</InfoPopup>}
              </th>
              {cancellationPerks.possibleChoices.map((possibleChoice, colIndex) => (
                <td
                  className={`perk-cell ${colIndex === 0 ? "most-popular" : ""}`}
                  key={`${rowIndex}-${colIndex}`}
                >
                  <div
                    className="perk-cell__content"
                    onClick={() => onChangeCancellationType(possibleChoice)}
                  >
                    {getPerkValue(perk)(possibleChoice)}
                  </div>
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <th />
            {cancellationPerks.possibleChoices.map((possibleChoice, index) => (
              <td className={`perk-cell ${index === 0 ? "most-popular" : ""}`} key={index}>
                <div
                  className="perk-cell__radio-container"
                  onClick={() => onChangeCancellationType(possibleChoice)}
                >
                  <Form.RadioField
                    checked={possibleChoice === selectedCancellationType}
                    name="cancellationType"
                    onChange={(event) =>
                      onChangeCancellationType(event.target.value as CancellationType)
                    }
                    value={possibleChoice}
                  />
                </div>
              </td>
            ))}
          </tr>
          {voucherFaqUrl && (
            <tr>
              <th />
              {cancellationPerks.possibleChoices.map((possibleChoice, index) => (
                <td className="perk-cell" key={index}>
                  <div className="perk-cell__link">
                    {index === 0 && (
                      <a href={voucherFaqUrl} rel="noopener noreferrer" target="_blank">
                        {t("lp_voucher_faq")}
                      </a>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DesktopPerksMatrix;
